import React from 'react';
import styled from 'styled-components';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { layout, position, PositionProps, space } from 'styled-system';
import { Theme, useTheme } from '../../../theme/theme';
import { LayoutProps, SpaceProps } from '../../../theme/styledProps';
import { Icon } from '../Icon/Icon';
import { RawButton } from '../Button/Button.styles';

// DOCS -> https://www.radix-ui.com/docs/primitives/components/popover

const Popover = PopoverPrimitive.Root;

Popover.defaultProps = {
  modal: false,
};

const PopoverTrigger = PopoverPrimitive.Trigger;

PopoverTrigger.defaultProps = {
  asChild: true,
};

const StyledPopoverTriggerButton = styled(RawButton)(({ theme }) => ({
  flexShrink: 0,
  padding: 4,
  display: 'inline-block',

  '&[data-focus-visible-added]:not(.disabled):focus': {
    boxShadow: `0px 0px 0px 2px ${theme.colors.primary['200']}, inset 0px 0px 0px 1px ${theme.colors.primary['500']}`,
    zIndex: 1,
  },
}));

/**
 * Useful when using non-buttons as triggers (e.g. Icons)
 */
const PopoverTriggerButton: React.FC<PopoverPrimitive.PopoverTriggerProps> = ({
  children,
  ...rest
}) => (
  <PopoverTrigger {...rest}>
    <StyledPopoverTriggerButton>{children}</StyledPopoverTriggerButton>
  </PopoverTrigger>
);

const PopoverContent = styled(PopoverPrimitive.Content)<
  { theme: Theme } & LayoutProps & PositionProps & SpaceProps
>(
  ({ theme }) => ({
    boxShadow: theme.shadows['500'],
    border: `1px solid ${theme.colors.neutral['200']}`,
    borderRadius: theme.radii.medium,
    background: theme.colors.bg,
    overflowY: 'auto',
    boxSizing: 'border-box',
    outline: 'none',
  }),

  layout,
  position,
  space,
);

PopoverContent.defaultProps = {
  sideOffset: 4,
};

const PopoverClose = PopoverPrimitive.Close;

PopoverClose.defaultProps = {
  asChild: true,
};

const StyledPopoverClose = styled(PopoverPrimitive.Close)(({ theme }) => ({
  all: 'unset',
  fontFamily: 'inherit',
  borderRadius: theme.radii.medium,
  height: 25,
  width: 25,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.neutral['500'],
  position: 'absolute',
  top: 8,
  right: 8,

  '&:hover': { backgroundColor: theme.colors.neutral['100'] },

  '&[data-focus-visible-added]:not(.disabled):focus': {
    boxShadow: `0px 0px 0px 2px ${theme.colors.primary['200']}, inset 0px 0px 0px 1px ${theme.colors.primary['500']}`,
    zIndex: 1,
  },
}));

const PopoverCloseButton = () => (
  <StyledPopoverClose aria-label="Close">
    <Icon className="fal fa-times" color="neutral.500" />
  </StyledPopoverClose>
);

const Arrow = props => {
  const theme = useTheme();

  return (
    <svg
      width={30}
      height={15}
      viewBox="0 0 30 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter="url(#filter0_i_6_8)">
        <path
          d="M16.4142 13.5858C15.6332 15 15 15 13.5858 13.5858L-2.26494e-06 3.4025e-08L30 2.65671e-06L16.4142 13.5858Z"
          fill={theme.colors.bg}
        />
      </g>
      <defs>
        <filter
          id="filter0_i_6_8"
          x={0}
          y={0}
          width={30}
          height={15}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity={0} result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={-1} />
          <feComposite in2="hardAlpha" operator="arithmetic" k2={-1} k3={1} />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.898039 0 0 0 0 0.905882 0 0 0 0 0.921569 0 0 0 1 0"
          />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_6_8" />
        </filter>
      </defs>
    </svg>
  );
};

const PopoverArrowContent = styled.div({
  display: 'flex',
  filter: 'drop-shadow(0 2px 1px rgba(55, 65, 81, 0.2))',
  transform: 'translateY(-3px)',
});

const PopoverArrow = () => {
  return (
    <PopoverPrimitive.Arrow asChild width={30} height={15}>
      <PopoverArrowContent>
        <Arrow />
      </PopoverArrowContent>
    </PopoverPrimitive.Arrow>
  );
};

export {
  Popover,
  PopoverTrigger,
  PopoverTriggerButton,
  PopoverContent,
  PopoverClose,
  PopoverCloseButton,
  PopoverArrow,
};
